/**
 * SidebarUserMenu
 *
 * Bottom-of-sidebar user identity. Shows the avatar + name + role; clicking
 * navigates to the user profile page (which hosts settings and Logout).
 *
 * Collapsed sidebar shows just the avatar circle; expanded sidebar reveals
 * the name and role beside it.
 */

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, ListItemButton, Typography } from '../ui';
import { RootState } from '../../types';
import { getFileUrl } from '../../hooks/function';
import { getRoleLabel } from '../../utils/roleHelpers';

export default function SidebarUserMenu() {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const [avatarUrl, setAvatarUrl] = useState<string>('/user.png');

  // Resolve profile image filename → SAS URL.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.profileImageUrl) {
        setAvatarUrl('/user.png');
        return;
      }
      try {
        const url = await getFileUrl(currentUser.profileImageUrl);
        if (!cancelled) setAvatarUrl(url || '/user.png');
      } catch {
        if (!cancelled) setAvatarUrl('/user.png');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.profileImageUrl]);

  if (!currentUser) return null;

  const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || 'User';
  // Undefined role → '' so the caption stays hidden rather than showing "Unknown".
  const role = currentUser.role != null ? getRoleLabel(currentUser.role) : '';

  return (
    <Box sx={{ mt: 'auto', width: '100%', borderTop: 1, borderColor: 'divider' }}>
      <ListItemButton
        onClick={() => navigate('/profile')}
        sx={{
          py: 1.5,
          px: 2,
          gap: 1.5,
          // Sidebar paper has overflowX: hidden + whitespace: nowrap, so the
          // name/role block clips cleanly when collapsed. Hover styling
          // comes from the theme's `action.hover` (pastel sky).
        }}
      >
        <Avatar src={avatarUrl} alt={fullName} sx={{ width: 36, height: 36, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {fullName}
          </Typography>
          {role && (
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {role}
            </Typography>
          )}
        </Box>
      </ListItemButton>
    </Box>
  );
}
